import { useQuery, useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";

import { useEffect, useMemo, useRef, useState } from "react";

import { usePathname } from "next/navigation";

import { useProgressNavigation } from "@/components/progress-bar";
import Create from "@/components/scribe/create";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Tooltip,
} from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";

import { queryClient } from "@/config/react-query";

import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";

import {
  buildInitialNoteAnswers,
  gatedSectionIds,
} from "@/lib/note-answer-prefill";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";

import { NoteType } from "@/types/enums/note-state";
import { NoteTemplateTableItem } from "@/types/notes";
import { PatientDetail } from "@/types/patient";

import { appointmentsQueryOptions } from "@/api/provider/appointments";
import { useCreateNoteMutation } from "@/api/provider/scribe/note-mutation";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
import {
  patientSidebarDefinition,
  selectPatientSidebar,
} from "@/features/sidebar/patient-sidebar";
import { useAppointmentLocationPromptStore } from "@/stores/appointment-location-prompt-store";
import {
  selectSidebarOpen,
  useRightSidebarStore,
} from "@/stores/right-sidebar-store";
import { useScribeNote, useScribeRecording } from "@/stores/scribe-store";

interface CreateNoteDialogProps {
  children?: React.ReactNode;
  patient?: PatientDetail | null;
  align?: "start" | "end";
  overrideLocationId?: string;
  onCreateNote?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Pre-select this note template when the dialog opens */
  defaultNoteTemplateId?: string | null;
  /** List these templates first in the picker (the appointment's services) */
  suggestedNoteTemplateIds?: string[];
  /** Link the created note to this appointment (e.g. from its checklist) */
  eventId?: string | null;
  /**
   * The appointment this note is likely for, when it isn't pinned with
   * `overrideLocationId`/`eventId`: only offers the switch to its location.
   */
  promptAppointment?: { id: string; locationId?: string | null } | null;
}

export default function CreateNoteWrapper({
  children,
  patient,
  align = "start",
  overrideLocationId,
  onCreateNote,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  defaultNoteTemplateId,
  suggestedNoteTemplateIds,
  eventId,
  promptAppointment,
}: CreateNoteDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled
    ? (o: boolean) => controlledOnOpenChange?.(o)
    : setInternalOpen;

  const [isCreating, setIsCreating] = useState(false);
  const { previouslySelectedNoteType } = useScribeNote();
  const [noteType, setNoteType] = useState<NoteType>(
    previouslySelectedNoteType,
  );
  const [patientId, setPatientId] = useState<string>();
  // The visit's templates once the user changes patient: the picked
  // appointment's from "Choose a patient from your appointments", or none for
  // a patient found by search. Unset while the dialog's own patient stands.
  const [pickedAppointmentTemplateIds, setPickedAppointmentTemplateIds] =
    useState<string[]>();
  const appointmentTemplateIds =
    pickedAppointmentTemplateIds ?? suggestedNoteTemplateIds;
  // The visit's template when it has exactly one: the dialog's own, or the
  // picked appointment's.
  const autoTemplateId = pickedAppointmentTemplateIds
    ? pickedAppointmentTemplateIds.length === 1
      ? pickedAppointmentTemplateIds[0]
      : null
    : (defaultNoteTemplateId ?? null);
  // A template the user chose; it outlasts a patient change.
  const [userTemplate, setUserTemplate] = useState<NoteTemplateTableItem>();
  const { tenant } = useTenant();
  const { currentUser, currentLocationId } = useUser();
  const requestLocationPrompt = useAppointmentLocationPromptStore(
    (state) => state.requestLocationPrompt,
  );
  const {
    handleNewNote,
    setPreviouslySelectedNoteType,
    fillFromLastNote,
    setFillFromLastNote,
    setDefaultNoteTemplateId,
  } = useScribeNote();
  const closeSidebar = useRightSidebarStore((state) => state.close);
  const isRightSidebarOpen = useRightSidebarStore(selectSidebarOpen);
  const sidebarPatient = useRightSidebarStore(selectPatientSidebar);
  const isSidebarOpen = isRightSidebarOpen && !!sidebarPatient.patientTiny;
  const { currentRecordingId } = useScribeRecording();
  const createNoteMutation = useCreateNoteMutation();
  const hookQueryClient = useQueryClient();
  const { navigateWithProgress } = useProgressNavigation();
  const pathname = usePathname();

  const { data: noteTemplates } = useQuery({
    ...noteQueryOptions.templates(),
    enabled: open,
  });
  const selectedTemplate =
    userTemplate ??
    (autoTemplateId
      ? noteTemplates?.find((t) => t.id === autoTemplateId)
      : undefined);

  const patientIdForPrefill = patientId ?? patient?.id;
  const hasShownPrefillErrorRef = useRef(false);
  const hasUserChangedPatientRef = useRef(false);

  const { data: prefilledAnswersData, isError: prefilledAnswersError } =
    useQuery({
      ...noteQueryOptions.prefilledAnswers(
        selectedTemplate?.id ?? "",
        patientIdForPrefill ?? "",
      ),
      enabled:
        noteType === NoteType.MANUAL &&
        !!selectedTemplate?.id &&
        !!patientIdForPrefill,
    });

  // Show warning toast if prefilled answers failed to load (only once per error)
  useEffect(() => {
    if (
      prefilledAnswersError &&
      selectedTemplate &&
      patientIdForPrefill &&
      !hasShownPrefillErrorRef.current
    ) {
      hasShownPrefillErrorRef.current = true;
      console.error(
        "Failed to fetch prefilled answers:",
        prefilledAnswersError,
      );
      toast({
        title: "Warning",
        description: "Some fields could not be pre-filled automatically",
        variant: "warning",
      });
    } else if (!prefilledAnswersError) {
      hasShownPrefillErrorRef.current = false;
    }
  }, [prefilledAnswersError, selectedTemplate, patientIdForPrefill]);

  const needsLocation = useMemo(() => {
    // If an override is provided, treat location requirement as satisfied
    if (overrideLocationId || !tenant?.enabledModules) return false;
    return tenant?.enabledModules?.includes("LOCATIONS") && !currentLocationId;
  }, [tenant?.enabledModules, currentLocationId, overrideLocationId]);

  useEffect(() => {
    if (open) {
      posthog.startSessionRecording({ sampling: true });
      if (patient?.id && !hasUserChangedPatientRef.current) {
        setPatientId(patient.id);
      }
    } else {
      // Reset template selection when dialog closes
      setUserTemplate(undefined);
      setPickedAppointmentTemplateIds(undefined);
      setPatientId(undefined);
      hasUserChangedPatientRef.current = false;
    }
  }, [open, patient?.id]);

  const handlePatientIdChange = (id: string) => {
    // Away from the dialog's own patient, its visit's templates no longer
    // apply; back on that patient they do again.
    const switched = !!id && id !== patient?.id;
    if (switched) hasUserChangedPatientRef.current = true;
    setPickedAppointmentTemplateIds(switched ? [] : undefined);
    setPatientId(id);
  };

  // Opened for a visit (e.g. its checklist), rather than just a patient.
  const dialogHasVisit =
    !!eventId || !!defaultNoteTemplateId || !!suggestedNoteTemplateIds?.length;
  // A patient picked from "Choose a patient from your appointments": their
  // in-window visits' templates become the suggestions. A dialog opened for a
  // visit keeps it when its own patient is picked.
  const handleAppointmentPicked = (
    pickedPatientId: string,
    templateIds: string[],
  ) => {
    if (pickedPatientId === patient?.id && dialogHasVisit) return;
    setPickedAppointmentTemplateIds(templateIds);
  };

  const handleCreateNote = async (noteType: NoteType) => {
    if (currentRecordingId) {
      toast({
        title: "Error",
        description: "Please stop the recording to create a note",
        variant: "error",
      });
      return;
    }

    const effectivePatientId = patientId ?? patient?.id;
    let patientToUse =
      patient && patient.id === effectivePatientId ? patient : undefined;
    if (effectivePatientId && !patientToUse) {
      const fetched = await queryClient.fetchQuery(
        patientQueryOptions.detail(effectivePatientId),
      );
      patientToUse = fetched || undefined;
    }

    if (!patientToUse || !currentUser) {
      toast({
        title: "Error",
        description: "Please select a patient to create a note",
        variant: "error",
      });
      return;
    }

    // Check if location is required and selected
    if (needsLocation) {
      // If location is required but not selected, fall back to dialog
      toast({
        title: "Error",
        description: "Please select a location to create a note",
        variant: "error",
      });
      return;
    }

    if (isSidebarOpen) {
      closeSidebar(patientSidebarDefinition.id);
    }

    // Only link the checklist appointment if the patient wasn't switched.
    const linkedEventId =
      eventId && patient && patientToUse.id === patient.id
        ? eventId
        : undefined;

    // For manual notes, create server note immediately (no local_id)
    if (noteType === NoteType.MANUAL) {
      // Require template selection for manual notes
      if (!selectedTemplate) {
        toast({
          title: "Error",
          description: "Please select a template to create a manual note",
          variant: "error",
        });
        return;
      }

      setIsCreating(true);
      try {
        // Joins the fetch started when the template was picked, so the button
        // never waits on it before the click.
        const prefill = prefilledAnswersError
          ? undefined
          : await hookQueryClient
              .ensureQueryData(
                noteQueryOptions.prefilledAnswers(
                  selectedTemplate.id,
                  patientToUse.id,
                ),
              )
              .catch(() => undefined);
        // Chart values go last so they win if both supply a question.
        const answers = buildInitialNoteAnswers(
          selectedTemplate.questions,
          [
            ...(fillFromLastNote ? (prefill?.lastNote?.answers ?? []) : []),
            ...(prefill?.prefilledAnswers ?? []),
          ],
          gatedSectionIds(selectedTemplate.sections),
        );

        const serverNote = await createNoteMutation.mutateAsync({
          patientId: patientToUse.id,
          providerId: currentUser.id,
          parentNoteId: undefined,
          eventId: linkedEventId,
          noteTranscriptId: undefined,
          aiGenerated: false,
          locationId: overrideLocationId || currentLocationId || undefined,
          template: selectedTemplate,
          answers,
        });

        if (!serverNote) {
          setIsCreating(false);
          return;
        }

        // Close modal after successful creation
        setOpen(false);
        setIsCreating(false);

        navigateWithProgress(
          `/scribe/playground/${serverNote.id}?type=${noteType}`,
        );
        onCreateNote?.();
        // Reset template selection after successful creation
        setUserTemplate(undefined);
      } catch (error) {
        console.error("Failed to create manual note:", error);
        setIsCreating(false);
      }
    } else {
      // For AI/Upload notes, use existing local note flow.
      // Upload has no event_id on /notes/upload, so only AI carries the link.
      const noteTemplateId = userTemplate?.id ?? autoTemplateId;
      // The quick action skips the dialog that offers the switch to the
      // appointment's location, and recording starts under the user's own
      // location, so offer it here. The app-level prompt outlives the
      // sidebar closing and the move to the patient.
      if (!open && overrideLocationId) {
        requestLocationPrompt(overrideLocationId, linkedEventId);
      } else if (!open && promptAppointment) {
        if (promptAppointment.locationId) {
          requestLocationPrompt(
            promptAppointment.locationId,
            promptAppointment.id,
          );
        }
      } else if (!open) {
        // No appointment given (e.g. the patient header): like the dialog,
        // use the one location of this patient's visits in the current window.
        const quickPatientId = patientToUse.id;
        hookQueryClient
          .fetchQuery(
            appointmentsQueryOptions.inWindow({ providerId: currentUser.id }),
          )
          .then((patientsInWindow) => {
            const appointments =
              patientsInWindow?.find(
                ({ patient: windowPatient }) =>
                  windowPatient.id === quickPatientId,
              )?.appointments ?? [];
            const locationIds = new Set(
              appointments
                .map((appointment) => appointment.location?.id)
                .filter((id): id is string => !!id),
            );
            if (locationIds.size !== 1) return;
            requestLocationPrompt(
              [...locationIds][0],
              appointments.length === 1 ? appointments[0].eventId : undefined,
            );
          })
          .catch(() => undefined);
      }
      setOpen(false);
      setDefaultNoteTemplateId(noteTemplateId);
      const note = handleNewNote(
        patientToUse,
        currentUser,
        noteType,
        noteType === NoteType.UPLOAD ? undefined : linkedEventId,
        noteType === NoteType.UPLOAD ? undefined : noteTemplateId,
        noteType === NoteType.UPLOAD ? undefined : appointmentTemplateIds,
      );

      const templateParam = noteTemplateId
        ? `&noteTemplateId=${noteTemplateId}`
        : "";
      if (noteType === NoteType.UPLOAD || tenant?.subDomain === "refinery") {
        navigateWithProgress(
          `/scribe/playground/${note.id}?type=${noteType}${templateParam}`,
        );
        onCreateNote?.();
      } else {
        if (!pathname.includes("patient")) {
          navigateWithProgress(`/patient/${patientToUse.id}`);
          onCreateNote?.();
        }
      }
    }
  };
  const hasAiScribeUsePermission = useMemo(
    () => hasPermission(currentUser, PERMISSIONS.AI_SCRIBE_USE),
    [currentUser],
  );
  const canCreateNotes = useMemo(
    () => hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE),
    [currentUser],
  );
  const canUploadNotes = useMemo(
    () => hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_UPLOAD),
    [currentUser],
  );
  const isPatientOptedOutOfAi = patient?.aiScribeConsent === false;
  // AI notes require write permission — the server's /start-recording route enforces
  // RequirePatientsNotesWrite — so an AI-capable but write-less role (e.g. upload-only)
  // must not be routed into the AI/Manual quick actions. Gating the branch on
  // canCreateNotes sends those roles to the dialog, where Upload is the allowed path.
  const aiNoteAvailable =
    canCreateNotes && hasAiScribeUsePermission && !isPatientOptedOutOfAi;
  const showDropdownWithDisabledAi =
    canCreateNotes && hasAiScribeUsePermission && isPatientOptedOutOfAi;

  const handleNoteTypeChange = (type: NoteType) => {
    setNoteType(type); // Update local state
    setPreviouslySelectedNoteType(type); // Update global store
    // Reset template selection when switching note types
    if (type !== NoteType.MANUAL) {
      setUserTemplate(undefined);
    }
  };

  // Hide the note-creation entry point only when the user can neither write nor
  // upload notes, regardless of which entry point mounts this wrapper. Upload is
  // gated separately (PATIENTS_NOTES_UPLOAD) and the backend /notes/upload route
  // enforces it independently of write, so an upload-only role must still reach
  // the dialog's Upload option. The server enforces write via
  // RequirePatientsNotesWrite on the note-creating routes.
  if (!canCreateNotes && !canUploadNotes) {
    return null;
  }

  if (patient && !needsLocation && !open) {
    if (!aiNoteAvailable && !showDropdownWithDisabledAi) {
      // No AI quick action for this role: open the dialog on the note type the user
      // can actually create — Manual for write roles, Upload for upload-only roles —
      // instead of hardcoding Manual (which would drop an upload-only role onto a
      // write-gated flow). The dialog still lets them switch among allowed types.
      return (
        <div
          role="button"
          tabIndex={0}
          className="active:scale-100"
          onClick={() => {
            if (!isCreating) {
              setNoteType(canCreateNotes ? NoteType.MANUAL : NoteType.UPLOAD);
              setOpen(true);
            }
          }}
          style={{
            opacity: isCreating ? 0.6 : 1,
            pointerEvents: isCreating ? "none" : "auto",
          }}
        >
          {children}
        </div>
      );
    }
    return (
      <DropdownMenu
        onOpenChange={(isOpen) => {
          if (isOpen) {
            posthog.startSessionRecording({ sampling: true });
          }
        }}
      >
        <Tooltip
          content="Please stop the recording to create a note"
          triggerAsChild
          className={cx(!currentRecordingId && "hidden")}
        >
          <DropdownMenuTrigger
            asChild
            className="active:scale-100"
            disabled={!!currentRecordingId}
          >
            {children}
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent align={align}>
          <Tooltip
            content="This patient has opted out of AI scribe"
            triggerAsChild
            className={cx(!isPatientOptedOutOfAi && "hidden")}
          >
            <DropdownMenuItem
              onClick={(e) => {
                if (isPatientOptedOutOfAi) {
                  e.preventDefault();
                  return;
                }
                if (!isCreating) handleCreateNote(NoteType.AI);
              }}
              disabled={isCreating || isPatientOptedOutOfAi}
            >
              <span>
                AI Note
                {isPatientOptedOutOfAi && (
                  <span className="text-muted-foreground text-caption">
                    {" "}
                    (Patient opted out)
                  </span>
                )}
              </span>
            </DropdownMenuItem>
          </Tooltip>
          <DropdownMenuItem
            onClick={() => {
              if (!isCreating) {
                setNoteType(NoteType.MANUAL);
                setOpen(true);
              }
            }}
            disabled={isCreating}
          >
            Manual Note
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && (
        <DialogTrigger asChild disabled={!!currentRecordingId}>
          {children}
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start a New Note</DialogTitle>
          <DialogDescription>
            Select a patient to begin creating a new clinical note
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="pb-8">
          <Create
            setNoteType={handleNoteTypeChange}
            noteType={noteType}
            setPatientId={handlePatientIdChange}
            patientId={patientId}
            existingPatient={patient ?? undefined}
            overrideLocationId={overrideLocationId}
            eventId={eventId}
            promptAppointment={promptAppointment}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setUserTemplate}
            suggestedTemplateIds={appointmentTemplateIds}
            onAppointmentPicked={handleAppointmentPicked}
            isDialogOpen={open}
            lastNote={prefilledAnswersData?.lastNote}
            fillFromLastNote={fillFromLastNote}
            setFillFromLastNote={setFillFromLastNote}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setOpen(false);
              setUserTemplate(undefined);
            }}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => handleCreateNote(noteType)}
            isLoading={isCreating}
            loadingText="Creating..."
            disabled={noteType === NoteType.MANUAL && !selectedTemplate}
          >
            Create Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
